import Loading from '@/components/Loading';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import ObservationScore from '@/components/UI/ObservationScore';
import type { ScoreType } from '@/components/UI/ObservationScore/ObservationScore';
import UserAvatar from '@/components/UI/UserAvatar';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { AnimalApiId } from '@/model/Animal.model';
import type { ISODateTime } from '@/model/Common.model';
import type { ObservationV1 } from '@/model/Observation.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { DateRenderFormat, DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils';
import { ModalContainer, ModalHeader, useModalAction } from '@/utils/modal';
import type { FC } from 'react';
import type { BaseUser } from '@/model/User.model';

interface LatestObservationsModalProps {
  animalApiId: AnimalApiId;
  animalName: string;
  latestObservedAt: ISODateTime;
}

interface ObservationCardProps {
  observation: ObservationV1;
}

const ObservationCard: FC<ObservationCardProps> = ({ observation }) => {
  const createdBy = observation?.created_at;
  const user: Pick<BaseUser, 'name'> = {
    name: observation?.created_at?.name ?? '',
  };

  return (
    <div className="mb2 bg-near-white" data-test-component="ObservationCard" data-test-element="container">
      <div className="pa3">
        <div className="flex justify-between items-center pb2">
          <span className="fw5 near-black" data-test-element="title">
            {observation.title}
          </span>
          {_notNil(observation.value) && (
            <span data-test-element="score">
              <ObservationScore
                type={observation.type}
                score={observation.value as ScoreType}
                options={observation.options ? (observation.options as { min: null; max: null }) : undefined}
              />
            </span>
          )}
        </div>

        {_isNotEmpty(observation.text) && (
          <div className="mt2 f6 mid-gray w-90" data-test-element="comment">
            {observation.text}
          </div>
        )}

        <div className="flex justify-between items-end f6 pt3">
          <span data-test-element="time">
            <DateTimeRenderer value={observation.observed_at} format={DateTimeRenderFormat.Time} />
          </span>
          <div className="flex items-end">
            {_notNil(observation.updated_at) && (
              <div className="pt3 pr2 f7 mid-gray" data-test-element="updated-at">
                Edited {<DateTimeRenderer value={observation.updated_at.datetime} />} by {observation.updated_at.name}
              </div>
            )}
            {_notNil(createdBy) && <UserAvatar user={user} tooltip={user.name} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export const LatestObservations: FC<LatestObservationsModalProps> = ({ animalApiId, animalName, latestObservedAt }) => {
  const { closeModal } = useModalAction();

  const { loading, response } = useApiHook({
    endpoint: 'GET /api/v1/animals/{animalApiId}/observations-on-date',
    path: { animalApiId },
  });

  const formattedDate = DateUtils.renderDateTime(latestObservedAt, {
    defaultResponse: '',
    format: DateRenderFormat.Date,
  });
  const title = _isNotEmpty(formattedDate) ? `${animalName} - ${formattedDate}` : animalName;
  const observations: Array<ObservationV1> = response?.body?.data ?? [];

  return (
    <ModalContainer size="narrow" testKey="latest-observations-modal">
      <ModalHeader title={title} closeModal={closeModal} />
      <div
        className="pa3"
        style={{ maxHeight: '60vh', overflowY: 'auto' }}
        data-test-component="LatestObservations"
        data-test-element="content"
      >
        {loading ? (
          <Loading />
        ) : observations.length === 0 ? (
          <p className="gray tc" data-test-element="empty-state">
            No observations recorded
          </p>
        ) : (
          observations.map((observation) => <ObservationCard key={observation.api_id} observation={observation} />)
        )}
      </div>
    </ModalContainer>
  );
};
